import React, { useState, useEffect, useRef } from 'react';
import {
  Paper, Typography, TableBody, TableCell, TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Autocomplete, Box, Grid, Alert, TablePagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import ListViewControls from '../ListViewControls';


// Mirrors backend/utils/accountCategories.js.
const ACCOUNT_CATEGORY_LABELS = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money', other: 'Other' };

const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);

  // SERVER-SIDE, all of it. The list pages at fifty, so a search or a filter applied here
  // would only ever see the page - an expense on page seven would read as one that does not
  // exist. Screens that load every row can afford to filter in the browser; this one cannot.
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalExpenses, setTotalExpenses] = useState(0);
  // Counted by the server over the whole filtered set. Derived from the rows on screen it
  // would describe the page, and "3 pending approval" out of three hundred is worse than
  // saying nothing.
  const [summary, setSummary] = useState({ pendingApproval: 0, ledgerPostings: 0 });
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { formatMoney: formatCurrency } = useCurrency();
  const [formData, setFormData] = useState({
    // Empty rather than a hardcoded default: categories are data now, so there is no
    // value we can assume exists.
    category: '',
    // No default: agency vs landlord decides whose books this lands on, so the user has
    // to say. Defaulting either way would quietly misstate someone's position.
    expenseType: '',
    paymentModeId: '',
    paidFromAccountId: '',
    amount: '',
    expenseDate: new Date().toISOString().split('T')[0],
    description: '',
    property: '',
    unit: '',
    vendor: '',
  });
  const [status, setStatus] = useState('recorded');
  const [statusFilter, setStatusFilter] = useState('all');
  // Which set of records the table is showing, and the date window over it. These go to
  // the server, unlike the search above — a deleted expense is not in the list at all
  // until the server is asked for it.
  const [view, setView] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [categories, setCategories] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [assetAccounts, setAssetAccounts] = useState([]);

  useEffect(() => {
    fetchExpenses();
    fetchProperties();
    fetchVendors();
    fetchCategories();
    fetchPaymentModes();
    fetchAssetAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The same modes payments use — "Bank Transfer" means the same thing whichever
  // direction the money moves.
  const fetchPaymentModes = async () => {
    try {
      const response = await axios.get('/payment-modes?isActive=true');
      setPaymentModes(response.data || []);
    } catch (error) {
      setPaymentModes([]);
    }
  };

  const fetchAssetAccounts = async () => {
    try {
      const response = await axios.get('/accounting/chart-of-accounts');
      const all = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setAssetAccounts(all.filter((a) => a.type === 'asset' && a.isActive !== false));
    } catch (error) {
      setAssetAccounts([]);
    }
  };

  // Categories come from the admin-managed table. This dropdown used to be a hardcoded
  // list, so anything an admin added under Expense Categories never appeared here.
  const fetchCategories = async () => {
    try {
      const response = await axios.get('/expense-categories?isActive=true');
      setCategories(response.data || []);
    } catch (error) {
      toast.error('Error fetching expense categories');
    }
  };

  const fetchExpenses = async (targetPage = page, targetSize = rowsPerPage, overrides = {}) => {
    const nextView = overrides.view ?? view;
    const nextFrom = overrides.from ?? dateRange.from;
    const nextTo = overrides.to ?? dateRange.to;
    const nextSearch = overrides.search ?? query;
    const nextStatus = overrides.status ?? statusFilter;
    const nextType = overrides.expenseType ?? typeFilter;
    try {
      const response = await axios.get('/expenses', {
        params: {
          page: targetPage + 1,
          limit: targetSize,
          // 'all' is the server's default, so it is left off rather than sent as a no-op.
          ...(nextView && nextView !== 'all' ? { view: nextView } : {}),
          ...(nextFrom ? { from: nextFrom } : {}),
          ...(nextTo ? { to: nextTo } : {}),
          ...(nextSearch ? { search: nextSearch } : {}),
          ...(nextStatus && nextStatus !== 'all' ? { status: nextStatus } : {}),
          ...(nextType ? { expenseType: nextType } : {})
        }
      });
      const body = response.data;
      // Tolerates the bare array the route used to return, so a stale cached bundle does
      // not render an empty table against a freshly deployed server.
      if (Array.isArray(body)) {
        setExpenses(body);
        setTotalExpenses(body.length);
        setSummary({
          pendingApproval: body.filter((e) => e.status === 'recorded').length,
          ledgerPostings: body.filter((e) => e.origin === 'ledger' || e.readOnly === true).length
        });
      } else {
        setExpenses(body.data || []);
        setTotalExpenses(body.total ?? (body.data || []).length);
        setSummary(body.summary || { pendingApproval: 0, ledgerPostings: 0 });
      }
    } catch (error) {
      toast.error('Error fetching expenses');
    }
  };

  // Debounced: a request per keystroke races, and the answer for "roo" can land after the
  // answer for "roof".
  //
  // Skipped on mount. The effect below already loads the first page, and without this guard
  // every visit to the screen fetches it twice - once on mount and again when this fires
  // with an empty search box.
  const searchSettled = useRef(false);
  useEffect(() => {
    if (!searchSettled.current) { searchSettled.current = true; return; }
    const timer = setTimeout(() => {
      setPage(0);
      fetchExpenses(0, rowsPerPage, { search: query });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Any filter change is a new result set, so it starts again at page one. Staying on
  // page 4 of a list that now has two pages shows an empty table.
  const applyFilter = (changes) => {
    setPage(0);
    fetchExpenses(0, rowsPerPage, changes);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchExpenses(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    setPage(0);
    fetchExpenses(0, size);
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/properties');
      setProperties(response.data);
    } catch (error) {
      toast.error('Error fetching properties');
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/vendors?isActive=true');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      // Don't show error if vendors endpoint doesn't exist yet
    }
  };

  const fetchUnitsForProperty = async (propertyId) => {
    if (!propertyId) {
      setUnits([]);
      return;
    }
    try {
      const response = await axios.get(`/units?property=${propertyId}`);
      setUnits(response.data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
      setUnits([]);
    }
  };

  const handlePropertyChange = (propertyId) => {
    setFormData({ ...formData, property: propertyId, unit: '' });
    fetchUnitsForProperty(propertyId);
  };

  const handleOpen = (expense = null) => {
    if (expense) {
      setEditing(expense._id);
      setFormData({
        category: expense.category,
        expenseType: expense.expenseType || '',
        paymentModeId: expense.paymentMode?._id || expense.paymentModeId || '',
        paidFromAccountId: expense.paidFromAccount?._id || expense.paidFromAccountId || '',
        amount: expense.amount,
        expenseDate: format(new Date(expense.expenseDate), 'yyyy-MM-dd'),
        description: expense.description,
        property: expense.property?._id || '',
        unit: expense.unit?._id || '',
        vendor: expense.vendor?._id || '',
      });
      setStatus(expense.status);
      if (expense.property?._id) {
        fetchUnitsForProperty(expense.property._id);
      }
    } else {
      setEditing(null);
      setFormData({
        category: '',
        expenseType: '',
        paymentModeId: '',
        paidFromAccountId: '',
        amount: '',
        expenseDate: new Date().toISOString().split('T')[0],
        description: '',
        property: '',
        unit: '',
        vendor: '',
      });
      setStatus('recorded');
      setUnits([]);
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.category || !formData.amount || !formData.expenseDate || !formData.description) {
      toast.error('Please fill in all required fields (Category, Amount, Date and Description).');
      return;
    }
    if (!formData.expenseType) {
      toast.error('Please choose whether this is an agency expense or a landlord expense.');
      return;
    }
    // Only a landlord expense needs a property. It is recovered from that property's rent,
    // and the landlord it is charged to is read from it. The agency's own costs — office
    // rent, airtime, subscriptions — relate to no property, and requiring one meant picking
    // a landlord's building to get past this form.
    if (formData.expenseType === 'landlord' && !formData.property) {
      toast.error('A landlord expense is recovered from a property’s rent, so please choose the property.');
      return;
    }
    // Only enforced at approval, matching the server: that is when the journal entry is
    // written and the credit has to come out of a real account.
    if (editing && status === 'approved' && !formData.paidFromAccountId) {
      toast.error('Please record which account this expense was paid from');
      return;
    }
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        property: formData.property,
        unit: formData.unit || undefined,
        vendor: formData.vendor || undefined,
      };
      if (editing) {
        payload.status = status;
        await axios.put(`/expenses/${editing}`, payload);
        toast.success('Expense updated successfully!');
        if (status === 'approved') {
          toast.info('Journal entry posted to accounting!');
        }
      } else {
        await axios.post('/expenses', payload);
        toast.success('Expense recorded! Approve it to post journal entry.');
      }
      setOpen(false);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving expense');
    }
  };

  const handleApprove = async (expenseId) => {
    try {
      await axios.put(`/expenses/${expenseId}`, { status: 'approved' });
      toast.success('Expense approved! Journal entry posted to accounting.');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error approving expense');
    }
  };

  const handleReject = async (expenseId) => {
    if (!window.confirm('Are you sure you want to reject this expense?')) {
      return;
    }
    try {
      await axios.put(`/expenses/${expenseId}`, { status: 'rejected' });
      toast.success('Expense rejected.');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error rejecting expense');
    }
  };


  // Cancelling is not the same as the reverse the backend has always had. Reversing says
  // "the figures are wrong" and hands the expense back for correction; this says it should
  // never have been recorded. Both unpost it - only this one finishes it.
  const handleCancel = async (expense) => {
    const reason = window.prompt(
      `Cancel this expense of ${formatCurrency(expense.amount)}?\n\n`
      + (expense.journalEntry
        ? 'It is posted, so its journal entries will be reversed - including the charge to '
          + 'the landlord, if it was on their behalf. '
        : 'It has not been posted, so there are no ledger entries to reverse. ')
      + 'Why is it being cancelled?'
    );
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('A reason is required to cancel an expense');
      return;
    }
    try {
      const { data } = await axios.post(`/expenses/${expense._id}/cancel`, { reason });
      toast.success(data.message || 'Expense cancelled');
      fetchExpenses();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not cancel this expense');
    }
  };

  // Only for an expense that never reached the ledger. Anything posted - or posted and
  // since reversed - is refused by the server, because deleting it would orphan journal
  // entries describing a document that no longer exists.
  const handleDelete = async (expense) => {
    const reason = window.prompt(
      `Delete this expense of ${formatCurrency(expense.amount)}?\n\n`
      + 'It will stop appearing in the expense list but stays on record under the Deleted '
      + 'view. Why is it being deleted? (optional)'
    );
    if (reason === null) return;
    try {
      const { data } = await axios.delete(`/expenses/${expense._id}`, { data: { reason } });
      toast.success(data.message || 'Expense deleted');
      fetchExpenses();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not delete this expense');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      recorded: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
      // A cost that came straight off the ledger. Not 'approved' — nobody approved it —
      // but every bit as much on the books, so it is not styled as an afterthought either.
      posted: 'info',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    if (status === 'recorded') return 'Pending';
    if (status === 'approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'posted') return 'Posted';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // WHERE THIS ROW CAME FROM, and therefore what may be done to it.
  //
  // A transfer charge, depreciation, a disposal loss or a manual journal debits an expense
  // account without anybody recording an expense, so there is no document to approve, edit,
  // cancel or delete — only the posting itself, which is undone where it was made. Correct
  // one of these by reversing the thing that caused it: the transfer under Contra Entries,
  // the journal under Accounting.
  const isLedgerRow = (e) => e.origin === 'ledger' || e.readOnly === true;

  const LEDGER_ORIGIN_LABELS = {
    contra: 'Transfer charge',
    depreciation: 'Depreciation',
    asset_purchase: 'Asset posting',
    manual: 'Manual journal',
    adjustment: 'Adjustment',
  };
  const ledgerOriginLabel = (e) => LEDGER_ORIGIN_LABELS[e.sourceType] || 'System posted';

  // Expenses store the category NAME, which is already display-ready. This map only
  // covers rows still holding a slug from the old hardcoded dropdown — i.e. databases
  // where `npm run migrate:expense-categories` has not been run yet.
  const LEGACY_CATEGORY_LABELS = {
    'repairs': 'Repairs',
    'maintenance': 'Maintenance',
    'landscaping': 'Landscaping',
    'insurance': 'Insurance',
    'management_fee': 'Management Fee',
    'utilities': 'Utilities',
    'supplies': 'Supplies',
    'other': 'Other'
  };

  const getCategoryLabel = (category) => LEGACY_CATEGORY_LABELS[category] || category || '-';

  // Cancelled and deleted are different events with parallel records - one reverses a
  // posted expense out of the ledger, the other removes one that never reached it. Same
  // three questions either way, so the table reads whichever set applies.
  const showAudit = view === 'cancelled' || view === 'deleted';
  const auditLabel = view === 'deleted' ? 'Deleted' : 'Cancelled';
  const auditDate = (e) => (view === 'deleted' ? e.deletedAt : e.cancelledAt);
  const auditWho = (e) => (view === 'deleted' ? e.deletedBy?.name : e.cancelledBy?.name);
  const auditReason = (e) => (view === 'deleted' ? e.deletionReason : e.cancellationReason);

  // The chosen mode decides which accounts are on offer: pick "Bank" and only the bank
  // accounts appear. The server enforces the same match, so the two cannot drift.
  const selectedMode = paymentModes.find((m) => m._id === formData.paymentModeId) || null;
  const accountsForSelectedMode = selectedMode
    ? assetAccounts.filter((a) => a.accountCategory === selectedMode.accountCategory)
    : [];
  const selectedModeCategoryLabel = selectedMode
    ? (ACCOUNT_CATEGORY_LABELS[selectedMode.accountCategory] || selectedMode.accountCategory)
    : '';

  // Once an expense has been posted its journal entry exists in one of two very different
  // shapes, so it can no longer be reclassified between agency and landlord — the server
  // refuses it too. Reverse the entry first if it genuinely needs changing.
  const editingExpense = expenses.find((e) => e._id === editing) || null;
  const isPosted = !!(editingExpense?.journalEntryId || editingExpense?.journalEntry);

  // The picker lists active categories only. When editing an expense whose category has
  // since been retired — or which still holds an un-migrated legacy slug — that value is
  // added back, otherwise the Select renders blank and saving would change the category
  // without the user asking.
  const categoryOptions = (() => {
    const options = categories.map((c) => ({ value: c.name, label: c.name }));
    const current = formData.category;
    if (current && !options.some((o) => o.value === current)) {
      options.push({ value: current, label: `${getCategoryLabel(current)} (unavailable)` });
    }
    return options;
  })();

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5">Expense Management</Typography>
          <Typography variant="body2" color="text.secondary">
            {summary.pendingApproval} pending approval
          </Typography>
          {/* Said once, here, rather than left for somebody to work out from a row they
              cannot act on. Before this the list was the expenses table alone, so a bank
              charge sat in the profit and loss and nowhere on this screen. */}
          {summary.ledgerPostings > 0 && (
            <Typography variant="caption" color="text.secondary">
              Includes {summary.ledgerPostings} cost
              {summary.ledgerPostings === 1 ? '' : 's'} posted straight to the
              ledger — transfer charges, depreciation and the like. They are read-only.
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Filter by Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              applyFilter({ status: e.target.value });
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Expenses</MenuItem>
            <MenuItem value="recorded">Pending Approval</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            {/* Costs that reached an expense account without an expense being recorded.
                Worth isolating: they are the ones nobody on this screen entered, so
                "where did that come from?" is the usual question about them. */}
            <MenuItem value="posted">Posted to ledger only</MenuItem>
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add Expense
          </Button>
        </Box>
      </Box>
      {/* One table throughout - these switch which set of records it holds. Deleted rows
          are soft-deleted and excluded from every other view, so this is the only way to
          reach them. */}
      <ListViewControls
        view={view}
        onViewChange={(v) => {
          setView(v);
          // The status dropdown is the finer filter INSIDE a view; leaving it on
          // "Approved" while switching to Cancelled would show an empty table and read
          // as "nothing was cancelled".
          setStatusFilter('all');
          applyFilter({ view: v, status: 'all' });
        }}
        from={dateRange.from}
        to={dateRange.to}
        onDateChange={(next) => {
          setDateRange(next);
          applyFilter(next);
        }}
        dateLabel="Spent"
      />

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Description, vendor or property"
        onReset={() => {
          setQuery('');
          setStatusFilter('all');
          setTypeFilter('');
          setView('all');
          setDateRange({ from: '', to: '' });
          applyFilter({ view: 'all', from: '', to: '', search: '', status: 'all', expenseType: '' });
        }}
        // The server's count of everything matching, not the length of the page.
        resultCount={totalExpenses}
        totalCount={totalExpenses}
        filters={[
          { key: 'expenseType', label: 'Charged to', value: typeFilter,
            onChange: (v) => { setTypeFilter(v); applyFilter({ expenseType: v }); },
            options: [{ value: 'agency', label: 'Agency' }, { value: 'landlord', label: 'Landlord' }] }
        ]}
      />
      <ScrollTable size="small">
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Category</TableCell>
              <TableCell>Charged To</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Property</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Journal Entry</TableCell>
              {/* Only where they mean something - on a live expense all three are empty by
                  definition, and blank columns cost width the rest of the table needs. */}
              {showAudit && <TableCell>{auditLabel} on</TableCell>}
              {showAudit && <TableCell>By</TableCell>}
              {showAudit && <TableCell>Reason</TableCell>}
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showAudit ? 14 : 11} align="center">
                  {statusFilter === 'all' ? 'No expenses recorded.' : `No ${statusFilter} expenses.`}
                </TableCell>
              </TableRow>
            ) : (
              expenses
                .map((expense) => (
                <TableRow key={expense._id}>
                  <TableCell className="pin-left">
                    {getCategoryLabel(expense.category)}
                    {/* The account code, because a ledger row's "category" IS its account —
                        there is no separate classification, and the code is what somebody
                        checking it against the trial balance actually looks for. */}
                    {isLedgerRow(expense) && expense.accountCode && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {expense.accountCode}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={expense.expenseType === 'agency' ? 'Agency' : 'Landlord'}
                      color={expense.expenseType === 'agency' ? 'secondary' : 'primary'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatCurrency(expense.amount)}</TableCell>
                  <TableCell>{format(new Date(expense.expenseDate), 'MMM dd, yyyy')}</TableCell>
                  <TableCell>
                    {expense.description}
                    {isLedgerRow(expense) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {ledgerOriginLabel(expense)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{expense.property?.propertyName || 'N/A'}</TableCell>
                  <TableCell>{expense.unit?.unitNumber || '-'}</TableCell>
                  <TableCell>{expense.vendor?.name || '-'}</TableCell>
                  <TableCell>
                    {/* A ledger posting has no approval cycle to have a status in — what
                        matters about one is whether it still stands. Reversing a transfer
                        credits its charge straight back, but the credit is a separate row
                        dated the day it was made, so on a list filtered to the month the
                        charge was incurred it is not on screen at all. Without this the
                        charge looks exactly like a live cost. */}
                    {isLedgerRow(expense) && expense.isReversed ? (
                      <Chip label="Reversed" color="warning" size="small" />
                    ) : isLedgerRow(expense) && expense.isReversal ? (
                      <Chip label="Reversal" color="info" size="small" variant="outlined" />
                    ) : (
                      <Chip
                        label={getStatusLabel(expense.status)}
                        color={getStatusColor(expense.status)}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {/* A ledger-sourced row is judged on what happened to its entry, not on
                        whether one exists -- one always does, so "has a journal entry" would
                        read Posted on a charge the ledger has since taken back, directly
                        beside a Status column saying Reversed. */}
                    {isLedgerRow(expense) && expense.isReversed ? (
                      <Chip label="Reversed" color="warning" size="small" variant="outlined" />
                    ) : isLedgerRow(expense) && expense.isReversal ? (
                      <Chip label="Reversal" color="info" size="small" variant="outlined" />
                    ) : expense.journalEntry ? (
                      <Chip 
                        icon={<CheckCircleIcon />}
                        label="Posted" 
                        color="success" 
                        size="small" 
                        variant="outlined"
                      />
                    ) : expense.reversalEntryId ? (
                      /* Not the same as never having been posted, and saying so matters:
                         this expense DID hit the ledger and was taken back out, which is
                         why its cost no longer appears in any report. */
                      <Chip
                        label="Reversed"
                        color="warning"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label="Not Posted"
                        color="default"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  {showAudit && (
                    <TableCell>
                      {auditDate(expense)
                        ? format(new Date(auditDate(expense)), 'd MMM yyyy')
                        : <Typography variant="caption" color="text.secondary">not recorded</Typography>}
                    </TableCell>
                  )}
                  {showAudit && (
                    <TableCell>
                      {auditWho(expense)
                        || <Typography variant="caption" color="text.secondary">unknown</Typography>}
                    </TableCell>
                  )}
                  {showAudit && (
                    <TableCell>
                      {auditReason(expense)
                        || <Typography variant="caption" color="text.secondary">none given</Typography>}
                    </TableCell>
                  )}
                  <TableCell className="pin-right">
                    {/* NOTHING TO OFFER ON A LEDGER ROW, and the buttons are withheld rather
                        than disabled. There is no expenses record behind it, so Approve,
                        Edit, Cancel and Delete have nothing to act on — each would address
                        /expenses/<a journal line id> and 404. The cost is undone where it
                        was made: reverse the transfer, or the journal entry. */}
                    {isLedgerRow(expense) ? (
                      <Typography variant="caption" color="text.secondary">
                        Posted by the system — reverse the entry that created it
                      </Typography>
                    ) : (
                    <Box display="flex" gap={1}>
                      {expense.status === 'recorded' && view !== 'deleted' && (
                        <>
                          <Button 
                            size="small" 
                            variant="contained" 
                            color="success"
                            startIcon={<ThumbUpIcon />} 
                            onClick={() => handleApprove(expense._id)}
                          >
                            Approve
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="error"
                            startIcon={<ThumbDownIcon />} 
                            onClick={() => handleReject(expense._id)}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      <Button 
                        size="small" 
                        startIcon={<EditIcon />} 
                        onClick={() => handleOpen(expense)}
                        variant={expense.status === 'recorded' ? 'outlined' : 'text'}
                      >
                        {/* A cancelled expense cannot be edited — the server refuses it,
                            because cancelling clears the field every other guard keys off
                            and an edit could otherwise re-post it. Labelled for what it
                            actually does rather than offering an Edit that always fails. */}
                        {expense.status === 'recorded' || expense.status === 'cancelled' ? 'View' : 'Edit'}
                      </Button>
                      {/* Cancelling reverses a posted expense out of the ledger and closes
                          it. Withheld once it is already cancelled or deleted - the server
                          refuses both, and offering a button that only ever fails is worse
                          than not offering it. */}
                      {expense.status !== 'cancelled' && view !== 'deleted' && (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<CancelIcon />}
                          onClick={() => handleCancel(expense)}
                        >
                          Cancel
                        </Button>
                      )}
                      {/* Only for an expense that never touched the ledger. Once posted -
                          or posted and since reversed - both journal entries would be
                          orphaned, so the server refuses and cancelling is the way. */}
                      {!expense.journalEntry && !expense.reversalEntryId
                        && expense.status !== 'cancelled' && view !== 'deleted' && (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDelete(expense)}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>
      <TablePagination
        component="div"
        count={totalExpenses}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit/Review Expense' : 'Add New Expense'}</DialogTitle>
        <DialogContent>
          {editing && status === 'approved' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This expense is approved and posted to journal entries. Changing it may require reversing the entry.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                select
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                helperText={
                  categories.length === 0
                    ? 'No categories configured — add them under Admin Panel → Expense Categories'
                    : undefined
                }
              >
                {categoryOptions.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                select
                label="Charge To"
                value={formData.expenseType}
                onChange={(e) => setFormData({ ...formData, expenseType: e.target.value })}
                disabled={isPosted}
                helperText={
                  isPosted
                    ? 'Locked — this expense has already been posted to the ledger'
                    : 'Landlord expenses are recovered from rent proceeds; agency expenses stay on the agency books'
                }
              >
                <MenuItem value="landlord">Landlord expense</MenuItem>
                <MenuItem value="agency">Agency expense</MenuItem>
              </TextField>
            </Grid>
            {/* How the money went out and which account it came from — the mirror of what
                a payment records. The journal entry credits whatever is selected here. */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Paid By"
                value={formData.paymentModeId}
                onChange={(e) => {
                  // Changing the mode changes which accounts are on offer, so any account
                  // already chosen is cleared rather than left pointing at the wrong kind.
                  const mode = paymentModes.find((m) => m._id === e.target.value);
                  const matching = assetAccounts.filter((a) => a.accountCategory === mode?.accountCategory);
                  setFormData({
                    ...formData,
                    paymentModeId: e.target.value,
                    paidFromAccountId: matching.length === 1 ? (matching[0]._id || matching[0].id) : '',
                  });
                }}
                disabled={isPosted}
                helperText={
                  isPosted
                    ? 'Locked — already posted to the ledger'
                    : paymentModes.length === 0
                      ? 'No modes set up — an admin can add them under Payment Modes'
                      : 'How this expense was settled'
                }
              >
                {paymentModes.map((mode) => (
                  <MenuItem key={mode._id} value={mode._id}>{mode.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label={selectedModeCategoryLabel ? `${selectedModeCategoryLabel} Account` : 'Paid From'}
                value={formData.paidFromAccountId}
                onChange={(e) => setFormData({ ...formData, paidFromAccountId: e.target.value })}
                disabled={isPosted || !formData.paymentModeId}
                helperText={
                  isPosted
                    ? 'Locked — already posted to the ledger'
                    : !formData.paymentModeId
                      ? 'Choose how it was paid first'
                      : accountsForSelectedMode.length === 0
                        ? `No ${selectedModeCategoryLabel} accounts set up — an admin can add one in the Chart of Accounts`
                        : 'Which account the money came out of'
                }
              >
                {accountsForSelectedMode.map((account) => (
                  <MenuItem key={account._id || account.id} value={account._id || account.id}>
                    {account.code} — {account.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Expense Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.expenseDate}
                onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Vendor (Optional)"
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor._id} value={vendor._id}>
                    {vendor.name} ({vendor.type})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Plumbing leak in Penthouse A"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={properties}
                value={properties.find(p => p._id === formData.property) || null}
                getOptionLabel={(p) => `${p.propertyName}${p.addressStreet ? ` - ${p.addressStreet}` : ''}`}
                onChange={(_, val) => handlePropertyChange(val?._id || '')}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Property"
                    required={formData.expenseType === 'landlord'}
                    helperText={
                      formData.expenseType === 'agency'
                        ? 'Optional — leave blank for a cost that relates to no property'
                        : ' '
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={units}
                value={units.find(u => u._id === formData.unit) || null}
                getOptionLabel={(u) => `Unit ${u.unitNumber}`}
                onChange={(_, val) => setFormData({ ...formData, unit: val?._id || '' })}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                disabled={!formData.property}
                renderInput={(params) => (
                  <TextField {...params} label="Unit (Optional - for apartment blocks)" />
                )}
              />
            </Grid>
            {editing && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="recorded">Pending</MenuItem>
                  <MenuItem value="approved">Approved (Posts to Journal)</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Save Changes' : 'Add Expense'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ExpenseManagement;
